"use client";

import { useState, useEffect, useRef } from "react";
import {
  Typography,
  Button,
  Box,
  Container,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  LinearProgress,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import axios from "axios";

const DOMAIN = process.env.REACT_APP_DOMAIN;

const PartnershipForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    fullName: "",
    organization: "",
    designation: "",
    contactNumber: "",
    email: "",
    category: "",
    details: "",
    linkedinId: "",
    proposalFile: null,
  });

  const [loading, setLoading] = useState(false);
  const [resp, setResp] = useState("");
  const [toasterOpen, setToasterOpen] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterSeverity, setToasterSeverity] = useState("success");

  const carouselSteps = [
    {
      title: "Startups",
      color: "#7c3aed",
      description: "Connect with innovative startups and emerging businesses",
    },
    {
      title: "Investors",
      color: "#7c3aed",
      description: "Partner with investors and funding organizations",
    },
    {
      title: "Customers",
      color: "#7c3aed",
      description: "Engage with potential customers and market opportunities",
    },
  ];

  // Auto-slide carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % carouselSteps.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [carouselSteps.length]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const clearForm = () => {
    setFormData({
      fullName: "",
      organization: "",
      designation: "",
      contactNumber: "",
      email: "",
      category: "",
      details: "",
      linkedinId: "",
      proposalFile: null,
    });
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const showToaster = (message, severity = "success") => {
    setToasterMessage(message);
    setToasterSeverity(severity);
    setToasterOpen(true);
  };

  const handleCloseToaster = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setToasterOpen(false);
  };

  const handleUploadProposal = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, proposalFile: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = new FormData();
    data.append("fullName", formData.fullName);
    data.append("organization", formData.organization);
    data.append("designation", formData.designation);
    data.append("contactNo", formData.contactNumber);
    data.append("mailId", formData.email);
    data.append("MarkoKnowPartner", formData.category);
    data.append("details", formData.details);
    data.append("linkedinId", formData.linkedinId);
    if (formData.proposalFile) {
      data.append("file", formData.proposalFile);
    }

    setLoading(true);
    try {
      await axios.post(`${DOMAIN}/api/postPartner`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          authorization: window.localStorage.getItem("token"),
        },
      });
      setResp("Message Sent ✅");
      showToaster(
        "Thank you for your interest! We will reach out to you soon.",
        "success"
      );
      clearForm();
    } catch (err) {
      console.error(err);
      setResp("Internal Server Error ❌");
      showToaster("Internal Server Error. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        // Reduced height to ensure the next section is visible at the bottom
        minHeight: "85vh",
        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        display: "flex",
        alignItems: "center",
        pt: { xs: 4, md: 6 },
        pb: { xs: 6, md: 8 },
        position: "relative",
        overflow: "hidden"
      }}
    >
      {/* Animation for the scroll hint down arrow */}
      <style>{`
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {transform: translateY(0);}
          40% {transform: translateY(-10px);}
          60% {transform: translateY(-5px);}
        }
      `}</style>

      <Container maxWidth="xl">
        <Grid container spacing={4} alignItems="center" justifyContent="center">
          
          {/* Left Column: Compact Content & Carousel */}
          <Grid item xs={12} md={5}>
            <Box sx={{ pr: { md: 4 }, textAlign: { xs: "center", md: "left" } }}>
              <Typography
                sx={{ 
                  display: "inline-block", 
                  bgcolor: "rgba(130, 87, 230, 0.1)", // Light transparent purple
                  color: "#8257E6", 
                  px: 2, py: 0.5, 
                  borderRadius: 1, 
                  fontSize: "0.75rem", 
                  fontWeight: 800, 
                  letterSpacing: 1,
                  mb: 3
                }}
              >
                PARTNER PROGRAM
              </Typography>
              
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  color: "#0f172a",
                  mb: 1,
                  fontSize: { xs: "2rem", md: "2.8rem" },
                  lineHeight: 1.1
                }}
              >
                Partner with <span style={{ color: "#8257E6" }}>Markoknow</span>
              </Typography>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#8257E6",
                  mb: 2,
                  fontSize: { xs: "1.6rem", md: "2.2rem" },
                }}
              >
                Connect with {carouselSteps[currentStep].title}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "#475569",
                  mb: 4,
                  fontSize: "1.1rem",
                  lineHeight: 1.6,
                  maxWidth: "450px",
                  mx: { xs: "auto", md: 0 }
                }}
              >
                {carouselSteps[currentStep].description}
              </Typography>

              {/* Progress Indicators updated to #8257E6 */}
              <Box sx={{ display: "flex", gap: 1, justifyContent: { xs: "center", md: "flex-start" }, mb: 4 }}>
                {carouselSteps.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: index === currentStep ? 35 : 10,
                      height: 6,
                      borderRadius: 3,
                      transition: "all 0.4s ease",
                      bgcolor: index === currentStep ? "#8257E6" : "#cbd5e1",
                    }}
                  />
                ))}
              </Box>

              {/* Floating Scroll Hint with Down Arrow */}
              <Box 
                onClick={() => window.scrollBy({ top: 600, behavior: 'smooth' })}
                sx={{ 
                  display: {xs: "none", md: "flex"}, 
                  alignItems: "center", 
                  gap: 1.5, 
                  color: "#94a3b8", 
                  cursor: "pointer",
                  mt: 6,
                  animation: "bounce 2s infinite",
                  "&:hover": { color: "#8257E6" }
                }}
              >
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, letterSpacing: 0.5 }}>EXPLORE NOW</Typography>
                <Box 
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    border: "2px solid",
                    borderRadius: "50%",
                    p: 0.2
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 13l5 5 5-5M7 6l5 5 5-5" />
                  </svg>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Right Column: Narrow Form Card (500px max) */}
          <Grid item xs={12} md={5} sx={{ display: "flex", justifyContent: "center" }}>
            <Box 
              sx={{ 
                bgcolor: "#ffffff", 
                p: { xs: 3, md: 4 }, 
                borderRadius: 4, 
                boxShadow: "0 20px 50px -12px rgba(130, 87, 230, 0.15)",
                border: "1px solid rgba(226, 232, 240, 0.7)",
                width: "100%",
                maxWidth: "500px", // Standardized narrow width
                position: "relative"
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, color: "#1e293b", textAlign: "center" }}>
                Partnership Inquiry
              </Typography>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "350px" }}>
                  <CircularProgress sx={{ color: "#8257E6" }} />
                </Box>
              ) : (
                <Box
                  component="form"
                  sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  onSubmit={handleSubmit}
                >
                  <TextField fullWidth placeholder="Full Name" name="fullName" value={formData.fullName} onChange={handleInputChange} size="small" />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth placeholder="Organization" name="organization" value={formData.organization} onChange={handleInputChange} size="small" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth placeholder="Contact Number" name="contactNumber" value={formData.contactNumber} onChange={handleInputChange} size="small" />
                    </Grid>
                  </Grid>

                  <TextField fullWidth placeholder="Email Address" name="email" type="email" value={formData.email} onChange={handleInputChange} size="small" />
                  
                  <FormControl fullWidth size="small">
                    <Select name="category" value={formData.category} onChange={handleInputChange} displayEmpty>
                      <MenuItem value=""><em>Select Category</em></MenuItem>
                      <MenuItem value="startup">Startup</MenuItem>
                      <MenuItem value="investor">Investor</MenuItem>
                      <MenuItem value="customer">Customer</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField fullWidth placeholder="Additional Details" name="details" multiline rows={3} value={formData.details} onChange={handleInputChange} />
                  
                  <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={handleUploadProposal}
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                      sx={{ py: 1.2, borderRadius: 2, color: "#8257E6", borderColor: "#8257E6", fontWeight: 700, "&:hover": { borderColor: "#6D48C4", bgcolor: "rgba(130, 87, 230, 0.05)" } }}
                    >
                      {formData.proposalFile ? formData.proposalFile.name.substring(0, 15) + "..." : "UPLOAD PROPOSAL"}
                    </Button>
                    <Button
                      variant="contained"
                      type="submit"
                      fullWidth
                      sx={{ py: 1.2, borderRadius: 2, bgcolor: "#8257E6", fontWeight: 700, "&:hover": { bgcolor: "#6D48C4" } }}
                    >
                      SUBMIT REQUEST
                    </Button>
                  </Box>

                  {resp && (
                    <Typography textAlign="center" sx={{ mt: 1, fontSize: "0.85rem", fontWeight: 700 }} color={resp.includes("Error") ? "error" : "success.main"}>
                      {resp}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

        <Snackbar open={toasterOpen} autoHideDuration={4000} onClose={handleCloseToaster} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert severity={toasterSeverity} sx={{ width: "100%", borderRadius: 2, fontWeight: 600, bgcolor: toasterSeverity === "success" ? "#8257E6" : undefined, color: toasterSeverity === "success" ? "#fff" : undefined }}>
            {toasterMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default PartnershipForm;